import { type ClassValue, clsx } from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Size } from '@/hooks/index';
import { FormattedAddress, NullableAddressLookup, T_NavLink } from '@/types';

import { getLinkUrl } from './analytics';
import { FIXED_ICOMS_LIMIT } from './constants';
import { validateAddress } from './validations';

type ContentNode = {
  data: Record<string, any>;
  content?: ContentNode[];
  marks?: any[];
  value?: string;
  nodeType: string;
};

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export const getHeadingTag = (nodeType: string): string => {
  // Extract the heading number if it's a heading type
  const headingMatch = nodeType?.match(/heading-([1-6])/);

  if (headingMatch) {
    // Return the corresponding h tag (h1-h6)
    return `h${headingMatch[1]}`;
  }

  // Default to paragraph tag if not a heading
  return 'p';
};

export const parseContentNode = (node: ContentNode, className?: string): React.ReactElement => {
  // Prioritize the nodeType from the first child, but fall back to the parent's nodeType.
  const nodeType = node?.content?.[0]?.nodeType || node.nodeType;
  const nodeValue = node?.content?.[0]?.content?.[0].value || node.value;
  const Tag = getHeadingTag(nodeType) as keyof JSX.IntrinsicElements;
  return React.createElement(Tag, { className }, nodeValue);
};

export const parseText = (node: any) => {
  const nodeValue = node?.content?.[0]?.content?.[0].value || node.value;
  return nodeValue;
};

export const formatToDecimalPlaces = (
  value: number | string,
  decimalPlaces: number = 2
): string => {
  // Convert the input to a number
  const numberValue = typeof value === 'string' ? parseFloat(value) : value;

  // Return the original value if the conversion is invalid
  if (isNaN(numberValue)) {
    return value + '';
  }

  // Format the number to two decimal places
  return numberValue === 0
    ? (0)?.toFixed(decimalPlaces).padStart(decimalPlaces + 3, '0') // "00.00", "00.000", etc.
    : numberValue?.toFixed(decimalPlaces);
};
export const splitPrice = (price?: string): [string, string] => {
  // Split the price string by the decimal point
  if (price === undefined) return ['0', '0'];

  const [integralPart, decimalPart] = price.split('.');

  // If the decimal part is undefined, set it to an empty string
  return [integralPart, decimalPart !== undefined ? decimalPart : ''];
};

export const extractNavMenu = (sectionLinks: any): T_NavLink[] => {
  return (sectionLinks ?? [])?.map(
    ({ itemUrl, name, sectionLinks, sectionName, itemIcon, tooltip }: any) => ({
      text: name,
      link: itemUrl,
      sectionLinks: sectionLinks,
      sectionName: sectionName,
      itemIcon: itemIcon,
      tooltip: tooltip,
    })
  );
};

export const getCityValue = (town: any, region: string) => {
  let city = town || region;
  if (city && city.length > 25) {
    city = city.substring(0, 25);
  }
  return city;
};

export const validateInstallationAddress = (value: NullableAddressLookup) => {
  return validateAddress('Installation')(value);
};

export const checkValidAddress = (address: NullableAddressLookup): boolean => {
  if (!address) return false;
  return address.label !== '' && address.id !== '';
};

export const generateLineValues = (value: string): { line: string; newLinePrepend: string } => {
  const addressSplit = value.split(' ');
  let line = '';
  let newLinePrepend = '';

  for (let wordToAdd of addressSplit) {
    // + 1 for space
    if (line.length + wordToAdd.length + 1 < FIXED_ICOMS_LIMIT) {
      line = !line.length ? `${wordToAdd}` : `${line} ${wordToAdd}`;
    } else {
      // + 2 as need the space + the extra character to fit in the end
      if (line.length + 2 <= FIXED_ICOMS_LIMIT) {
        // can add to end - but must have space left
        let amountCanAppend = FIXED_ICOMS_LIMIT - (line.length + 1);
        line = `${line} ${wordToAdd.substring(0, amountCanAppend)}`;
        newLinePrepend = wordToAdd.substring(amountCanAppend);
      } else {
        newLinePrepend = newLinePrepend.length ? `${newLinePrepend} ${wordToAdd}` : `${wordToAdd}`;
      }
    }
  }
  return { line, newLinePrepend };
};

//

/**
 * Need to format the address as each line has 32 character icoms restriction
 */
export const formatAddress = (
  street: string,
  suburb: string,
  city: string,
  postcode: string,
  tui: string
): FormattedAddress => {
  const addressArray = [street ?? '', suburb ?? '', city ?? '', postcode ?? ''];
  const valid = addressArray.every(e => e.length <= FIXED_ICOMS_LIMIT);
  if (valid) {
    return {
      line3: addressArray[2],
      line4: addressArray[3],
      line1: addressArray[0],
      line2: addressArray[1],
      tui,
    };
  }

  // invalid case for icoms
  let array = ['', '', '', ''];
  for (let i = 0; i < addressArray.length; i++) {
    if (addressArray[i].length > FIXED_ICOMS_LIMIT) {
      let { line, newLinePrepend } = generateLineValues(addressArray[i]);
      // set the line
      array[i] = line;
      if (!newLinePrepend.length) continue; //skip if nothing to add in new line
      if (i + 1 < array.length) {
        addressArray[i + 1] =
          addressArray[i + 1].length > 0
            ? `${newLinePrepend} ${addressArray[i + 1]}`
            : `${newLinePrepend}`;
      } else {
        // ULTRA RARE / IMPOSSIBLE edge case. The last bit is just lost.
        // Really wouldn't expect this to happen
      }
    } else {
      // line meets requirement of 32 characters or less
      array[i] = addressArray[i];
    }
  }

  return {
    line1: array[0],
    line2: array[1],
    line3: array[2],
    line4: array[3],
    tui,
  };
};

export const uniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export const truncate = (text: string, maxLength: number) =>
  text.length > maxLength ? text.slice(0, maxLength) + '...' : text;

export const countHtmlTags = (str: string): number => {
  let count = 0;
  let cursor = 0;

  while (cursor < str.length) {
    const open = str.indexOf('<', cursor);
    if (open === -1 || open === str.length - 1) break;

    const nextChar = str[open + 1];
    // Ignore closing tags and comments/doctype-like entries
    if (nextChar === '/' || nextChar === '!') {
      cursor = open + 1;
      continue;
    }

    const close = str.indexOf('>', open + 1);
    if (close === -1) break;

    count += 1;
    cursor = close + 1;
  }

  return count;
};

export const containsMultipleHtmlTags = (str: string): boolean => {
  return countHtmlTags(str) > 1;
};

export const containsAnchorTag = (str: string) => {
  return /<a[^>]*>.*<\/a>/.test(str);
};

export const getAnchorTagText = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  const anchorTag = doc.querySelector('a');
  return anchorTag;
};

export const hasBTag = (str: string) => {
  return /<\s*(b|strong)\s*>.*?<\s*\/\s*(b|strong)\s*>/i.test(str);
};

export const hasItalic = (str: string) => {
  return /<\s*(i|em)\s*>.*?<\s*\/\s*(i|em)\s*>/i.test(str);
};

export const getBTagText = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  const boldTag = doc.querySelector('b, strong');
  return boldTag?.textContent || '';
};

export const getItalicTagText = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  const italicTag = doc.querySelector('i, em');
  return italicTag?.textContent || '';
};

export const extractTagStructureWithText = (html: string): any[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const processNode = (node: ChildNode): any => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      return {
        type: 'element',
        tag: el.tagName.toLowerCase(),
        children: Array.from(el.childNodes).map(processNode).filter(Boolean),
      };
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        return {
          type: 'text',
          content: text,
        };
      }
    }
    return null;
  };

  return Array.from(body.childNodes).map(processNode).filter(Boolean);
};

export const removeDuplicateFromArrayList = (lists: any[]) => {
  let flat = lists.reduce((acc, val) => acc.concat(val), []);
  let uniqueSet = new Set(flat);
  return Array.from(uniqueSet);
};

export const isShowFooter = (planTabIndex: number, pathname: string) => {
  let isShow = true;
  if ([0, 1, 2].includes(planTabIndex) && pathname.includes('/build-my-plan/checkout')) {
    isShow = false;
  } else if (pathname.includes('/plans/')) {
    isShow = false;
  }
  return isShow;
};
export const isHeaderBgSolid = (pathName: string): boolean => {
  return pathName.includes('/build-my-plan/checkout');
};

export const isValidUrl = (string: string) => {
  return (
    typeof string === 'string' &&
    /^https?:\/\/\S+$/.test(string) &&
    (() => {
      try {
        new URL(string);
        return true;
      } catch {
        return false;
      }
    })()
  );
};

export const removeWhitSpaceFromString = (str: string) =>
  str
    ?.toLocaleLowerCase()
    .trim()
    .replace(/[\u200B-\u200D\uFEFF]/g, '');

export const newDialogBoxSize = (size: Size): Size => {
  return ['sm', 'md', 'lg'].includes(size) ? size : 'lg';
};

export const isHiddenNotificationIcon = (size: Size): boolean => {
  return ['lg', 'xl', 'xxl'].includes(size);
};

export const identifyURLType = (link: string) => {
  if (!link)
    return {
      resolvedUrl: undefined,
      target: '_self',
      isPageAnchor: false,
      isExternalURl: false,
    };

  const isExternalURl = link.startsWith('http://') || link.startsWith('https://');
  const isPageAnchor = link.startsWith('#');
  const isRelativePath = link.startsWith('/');
  const isProtocolLink = link.startsWith('tel:') || link.startsWith('mailto:');

  // Directly set the resolvedUrl based on link type
  const resolvedUrl =
    isExternalURl || isPageAnchor || isRelativePath || isProtocolLink ? link : getLinkUrl(link);
  let target = isExternalURl ? '_blank' : '_self';

  return { resolvedUrl, target, isPageAnchor, isExternalURl, isProtocolLink };
};

export const parseDuration = (isoDuration: any) => {
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;

  const [, hours = 0, minutes = 0] = isoDuration.match(regex);

  return `${Number(hours)}h  ${Number(minutes)}min`;
};
export const restrictChars = (str: string, maxLength: number): React.ReactNode => {
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + '...';
  }
  return str;
};

export const packagesContext = (product: any) => {
  return {
    '233': `Device / ${product?.category}`,
    '202': `Device / ${product?.category}`,
    '238': `Device / ${product?.category}`,
    '615': `Device / ${product?.category}`,
    '624': `Device / ${product?.category}`,
    '616': `Device / ${product?.category}`,
    '887': `Device / ${product?.category}`,
    '830': `Device / ${product?.category}`,
    '242': `Device / ${product?.category}`,
    '801BB': `Device / ${product?.category}`,
    '800BB': `Device / ${product?.category}`,
    '700BB': 'upgrade / Sky Wifi Router',
    '604BB': 'Upgrade / Sky Wifi Booster / Plan Upgrade Popup',
    '199BB': `Add-ons / ${product?.category}`,
    '806BB': `Add-ons / ${product?.category}`,
    '102BB': `Add-ons / ${product?.category}`,
    '001TP': 'Add-ons / broadband / sky home phone plan / checkout addons popup',
    '002TP': 'Add-ons / broadband / sky home phone plan / checkout addons popup',
    '100TP': 'Add-ons / broadband / sky home phone plan / checkout addons popup',
    '102TP': 'Add-ons / broadband / sky home phone plan / checkout addons popup',
    '210TP': 'Add-ons / broadband / sky home phone plan / checkout addons popup',
    '200TP': 'Add-ons / broadband / sky home phone plan / checkout addons popup',
    '100BB': `Device / ${product?.category}`,
    '109BB': `Device / ${product?.category}`,
    '119BB': `Device / ${product?.category}`,
  } as any;
};

export const truncateText = (text: string, maxLength = 12, visibleLength = 9): string => {
  return text.length > maxLength ? `${text.slice(0, visibleLength)}...` : text;
};

export const a11yClick = <T extends HTMLElement>(e: React.KeyboardEvent<T>) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.currentTarget.click(); //triggers onClick of the respective HTML element
  }
};
